/**
 * /admin/admin — ADMIN (custos, limites e backups).
 *
 * Três blocos:
 *  1. CUSTOS — o que consome crédito de IA / infraestrutura no painel.
 *  2. LIMITES — métricas reais do banco/storage/sessões contra os limites do
 *     plano (editáveis e salvos em site_config → admin.limites).
 *  3. BACKUPS — zip do código-fonte do site e zip do banco de dados.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  ArrowLeft,
  Coins,
  Database,
  Download,
  Gauge,
  Github,
  HardDrive,
  Loader2,
  RefreshCw,
  Save,
  Sparkles,
  Server,
} from "lucide-react";
import { toast } from "sonner";

import { AdminGate } from "@/components/admin/AdminGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { CUSTOS, CUSTO_TIPO_LABEL, type CustoTipo } from "@/lib/admin/custos";
import {
  LIMITES_PADRAO,
  SEMAFORO_LABEL,
  recomendacao,
  semaforo,
  type LimitesConfig,
  type Semaforo,
} from "@/lib/admin/limites";
import { getAdminMetrics, saveAdminLimites } from "@/lib/admin/limites.functions";
import { formatBRTime } from "@/lib/datetime";
import { downloadBlob } from "@/lib/download";

export const Route = createFileRoute("/admin/admin")({
  head: () => ({
    meta: [
      { title: "Admin — Custos, limites e backups" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/admin">
      <AdminAdminPage />
    </AdminGate>
  ),
});

const TIPO_STYLE: Record<CustoTipo, string> = {
  ia: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/30",
  infra: "bg-amber-500/15 text-admin-accent border-amber-400/30",
  gratis: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
};

const SEM_STYLE: Record<Semaforo, string> = {
  ok: "text-emerald-300 bg-emerald-500/15 border-emerald-400/30",
  atencao: "text-admin-accent bg-amber-500/15 border-amber-400/30",
  critico: "text-red-300 bg-red-500/15 border-red-400/30",
};

const BAR_STYLE: Record<Semaforo, string> = {
  ok: "bg-emerald-400",
  atencao: "bg-amber-400",
  critico: "bg-red-400",
};

function nf(n: number): string {
  return n.toLocaleString("pt-BR");
}

function gb(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function AdminAdminPage() {
  const metricsFn = useServerFn(getAdminMetrics);
  const saveFn = useServerFn(saveAdminLimites);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["admin-metrics"],
    queryFn: () => metricsFn(),
    staleTime: 60_000,
  });

  const [form, setForm] = useState<LimitesConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [siteBusy, setSiteBusy] = useState<string | null>(null);
  const [dbBusy, setDbBusy] = useState<string | null>(null);

  const limites = form ?? data?.limites ?? LIMITES_PADRAO;

  function setField(k: keyof LimitesConfig, v: string) {
    const base = form ?? data?.limites ?? LIMITES_PADRAO;
    const next: LimitesConfig = { ...base };
    if (k === "planoNome") next.planoNome = v;
    else (next[k] as number) = Number(v.replace(/\D/g, "")) || 0;
    setForm(next);
  }

  async function salvar() {
    setSaving(true);
    try {
      await saveFn({
        data: {
          planoNome: limites.planoNome,
          creditosMes: limites.creditosMes,
          creditosUsados: limites.creditosUsados,
          armazenamentoGb: limites.armazenamentoGb,
          linhasBanco: limites.linhasBanco,
          sessoesMes: limites.sessoesMes,
        },
      });
      toast.success("Limites salvos");
      setForm(null);
      void refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  async function baixarSite() {
    setSiteBusy("Preparando…");
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error("Sessão expirada — faça login novamente.");
      const res = await fetch("/api/admin-site-backup", {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Falha (${res.status})`);
      const blob = await res.blob();
      downloadBlob(blob, `feo-site-${new Date().toISOString().slice(0, 10)}.zip`);
      toast.success("Backup do site baixado");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha no backup do site");
    } finally {
      setSiteBusy(null);
    }
  }

  async function baixarBanco(mode: "completo" | "operacao") {
    setDbBusy(mode);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error("Sessão expirada — faça login novamente.");
      const res = await fetch(`/api/admin-backup?mode=${mode}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Falha (${res.status})`);
      const blob = await res.blob();
      downloadBlob(blob, `feo-banco-${mode}-${new Date().toISOString().slice(0, 10)}.zip`);
      toast.success("Backup do banco baixado");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha no backup do banco");
    } finally {
      setDbBusy(null);
    }
  }

  const usoStorageGb = (data?.storageBytes ?? 0) / 1024 / 1024 / 1024;
  const indicadores = [
    {
      id: "creditos",
      label: "Créditos de IA no mês",
      icon: Sparkles,
      atual: limites.creditosUsados,
      limite: limites.creditosMes,
      texto: `${nf(limites.creditosUsados)} de ${nf(limites.creditosMes)}`,
      nota: "Preencha o consumo do mês olhando o painel de créditos do Lovable — o valor não vem por API.",
    },
    {
      id: "linhas",
      label: "Linhas no banco",
      icon: Database,
      atual: data?.totalLinhas ?? 0,
      limite: limites.linhasBanco,
      texto: `${nf(data?.totalLinhas ?? 0)} de ${nf(limites.linhasBanco)}`,
      nota: "Medido agora, somando todas as tabelas da operação.",
    },
    {
      id: "armazenamento",
      label: "Armazenamento de arquivos",
      icon: HardDrive,
      atual: usoStorageGb,
      limite: limites.armazenamentoGb,
      texto: `${gb(data?.storageBytes ?? 0)} de ${limites.armazenamentoGb} GB · ${nf(data?.storageArquivos ?? 0)} arquivo(s)`,
      nota: "PDFs de ementa e uploads do admin.",
    },
    {
      id: "sessoes",
      label: "Sessões no mês",
      icon: Server,
      atual: data?.sessoesMes ?? 0,
      limite: limites.sessoesMes,
      texto: `${nf(data?.sessoesMes ?? 0)} de ${nf(limites.sessoesMes)}`,
      nota: `${nf(data?.pageViewsMes ?? 0)} page views no mês.`,
    },
  ];

  return (
    <div className="min-h-screen text-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6">
          <ArrowLeft className="h-4 w-4" /> Voltar ao painel
        </Link>

        <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold uppercase tracking-tight">Admin</h1>
            <p className="text-sm text-white/60 mt-1 max-w-2xl">
              Custos das ferramentas do painel, limites da operação e backups do site e do banco.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {data && (
              <span className="text-xs text-white/50">Medido às {formatBRTime(data.geradoEm)}</span>
            )}
            <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} /> Atualizar
            </Button>
          </div>
        </header>

        {/* ——— 1. CUSTOS ——— */}
        <section className="mb-12">
          <div className="flex items-center gap-2 mb-1">
            <Coins className="h-5 w-5 text-fuchsia-300" />
            <h2 className="text-lg font-bold uppercase tracking-wide">1. O que tem custo</h2>
          </div>
          <p className="text-sm text-white/60 mb-4">
            Só as ferramentas marcadas como <strong className="text-fuchsia-300">crédito de IA</strong> gastam
            crédito do Lovable. Ler dados, filtrar e exportar CSV nunca custa nada.
          </p>

          <div className="grid gap-3">
            {(["ia", "infra", "gratis"] as CustoTipo[]).map((tipo) => (
              <div key={tipo} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className={`text-[11px] ${TIPO_STYLE[tipo]}`}>
                    {CUSTO_TIPO_LABEL[tipo]}
                  </Badge>
                  <span className="text-xs text-white/40">
                    {CUSTOS.filter((c) => c.tipo === tipo).length} item(ns)
                  </span>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {CUSTOS.filter((c) => c.tipo === tipo).map((c) => (
                    <div key={c.id} className="rounded-xl border border-white/10 bg-black/20 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold">{c.ferramenta}</h3>
                        <span className="text-[11px] text-white/40 whitespace-nowrap">{c.rota}</span>
                      </div>
                      <p className="text-xs text-white/60 mt-1">{c.gatilho}</p>
                      <p className="text-xs text-white/80 mt-1">
                        <strong>Custo:</strong> {c.estimativa}
                      </p>
                      {c.cuidado && (
                        <p className="text-xs text-amber-200/80 mt-1">
                          <strong>Cuidado:</strong> {c.cuidado}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ——— 2. LIMITES ——— */}
        <section className="mb-12">
          <div className="flex items-center gap-2 mb-1">
            <Gauge className="h-5 w-5 text-sky-300" />
            <h2 className="text-lg font-bold uppercase tracking-wide">2. Limites e saúde da operação</h2>
          </div>
          <p className="text-sm text-white/60 mb-4">
            Os números da esquerda são medidos agora no banco. Os limites são do seu plano e podem ser
            editados abaixo — ajuste sempre que o plano mudar.
          </p>

          <div className="grid sm:grid-cols-2 gap-3 mb-5">
            {indicadores.map((ind) => {
              const pct = ind.limite > 0 ? Math.min(100, (ind.atual / ind.limite) * 100) : 0;
              const s = semaforo(pct);
              const rec = recomendacao(ind.id, s);
              return (
                <div key={ind.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      <ind.icon className="h-4 w-4 text-white/60" /> {ind.label}
                    </span>
                    <Badge variant="outline" className={`text-[11px] ${SEM_STYLE[s]}`}>
                      {SEMAFORO_LABEL[s]}
                    </Badge>
                  </div>
                  <p className="text-lg font-bold mt-2">{ind.texto}</p>
                  <div className="h-2 rounded-full bg-white/10 mt-2 overflow-hidden">
                    <div className={`h-full ${BAR_STYLE[s]}`} style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[11px] text-white/50 mt-2">{ind.nota}</p>
                  {rec && <p className="text-[11px] text-amber-200/80 mt-1">{rec}</p>}
                </div>
              );
            })}
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 mb-5">
            <h3 className="text-sm font-semibold mb-3">Limites do plano (editável)</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <label className="text-xs text-white/60">
                Nome do plano
                <Input
                  className="mt-1 bg-black/30 border-white/15"
                  value={limites.planoNome}
                  onChange={(e) => setField("planoNome", e.target.value)}
                />
              </label>
              <label className="text-xs text-white/60">
                Créditos de IA por mês
                <Input
                  className="mt-1 bg-black/30 border-white/15"
                  value={String(limites.creditosMes)}
                  onChange={(e) => setField("creditosMes", e.target.value)}
                />
              </label>
              <label className="text-xs text-white/60">
                Créditos já usados no mês
                <Input
                  className="mt-1 bg-black/30 border-white/15"
                  value={String(limites.creditosUsados)}
                  onChange={(e) => setField("creditosUsados", e.target.value)}
                />
              </label>
              <label className="text-xs text-white/60">
                Armazenamento (GB)
                <Input
                  className="mt-1 bg-black/30 border-white/15"
                  value={String(limites.armazenamentoGb)}
                  onChange={(e) => setField("armazenamentoGb", e.target.value)}
                />
              </label>
              <label className="text-xs text-white/60">
                Limite de linhas no banco
                <Input
                  className="mt-1 bg-black/30 border-white/15"
                  value={String(limites.linhasBanco)}
                  onChange={(e) => setField("linhasBanco", e.target.value)}
                />
              </label>
              <label className="text-xs text-white/60">
                Sessões por mês
                <Input
                  className="mt-1 bg-black/30 border-white/15"
                  value={String(limites.sessoesMes)}
                  onChange={(e) => setField("sessoesMes", e.target.value)}
                />
              </label>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <Button size="sm" onClick={() => void salvar()} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Salvar limites
              </Button>
              {limites.atualizadoEm && (
                <span className="text-xs text-white/40">
                  Atualizado em {formatBRTime(limites.atualizadoEm)}
                </span>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <h3 className="text-sm font-semibold mb-1">Volume por tabela</h3>
            <p className="text-xs text-white/50 mb-3">
              Leads: {nf(data?.leadsTotal ?? 0)} no total · {nf(data?.leadsMes ?? 0)} no mês. Vendas:{" "}
              {nf(data?.vendasTotal ?? 0)} no total · {nf(data?.vendasMes ?? 0)} no mês.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase text-white/40">
                    <th className="py-2">Tabela</th>
                    <th className="py-2">Grupo</th>
                    <th className="py-2 text-right">Linhas</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.tabelas ?? []).map((t) => (
                    <tr key={t.tabela} className="border-t border-white/5">
                      <td className="py-1.5 font-mono text-xs">{t.tabela}</td>
                      <td className="py-1.5 text-xs text-white/50">{t.grupo}</td>
                      <td className="py-1.5 text-right">{nf(t.linhas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ——— 3. BACKUPS ——— */}
        <section>
          <div className="flex items-center gap-2 mb-1">
            <Download className="h-5 w-5 text-emerald-300" />
            <h2 className="text-lg font-bold uppercase tracking-wide">3. Backups</h2>
          </div>
          <p className="text-sm text-white/60 mb-4">
            Guarde uma cópia fora do Lovable. Recomendado: 1 vez por mês, ou antes de mudanças grandes.
          </p>

          <div className="grid md:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <h3 className="text-sm font-semibold">Backup do site (código-fonte)</h3>
              <p className="text-xs text-white/60 mt-1">
                .zip com todas as páginas, componentes, estilos, imagens e arquivos de configuração —
                pronto para rodar em outra plataforma.
              </p>
              <Button
                size="sm"
                className="mt-3"
                onClick={() => void baixarSite()}
                disabled={!!siteBusy}
              >
                {siteBusy ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {siteBusy ?? "Baixar .zip do site"}
              </Button>
              <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-3">
                <p className="flex items-center gap-2 text-xs font-semibold">
                  <Github className="h-3.5 w-3.5" /> Cópia oficial no GitHub (recomendado)
                </p>
                <ol className="text-[11px] text-white/60 mt-2 space-y-1 list-decimal list-inside">
                  <li>No editor do Lovable, abra o menu do projeto (canto superior direito).</li>
                  <li>Escolha GitHub → Connect to GitHub e autorize a conta.</li>
                  <li>Crie o repositório — o código passa a ser sincronizado a cada alteração.</li>
                  <li>Com o repositório criado, dá para clonar/baixar o projeto inteiro com histórico.</li>
                </ol>
                <p className="text-[11px] text-white/40 mt-2">
                  O .zip acima é a alternativa imediata; o GitHub é a cópia contínua e fiel.
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <h3 className="text-sm font-semibold">Backup do banco / memória</h3>
              <p className="text-xs text-white/60 mt-1">
                .zip com CSV (Excel) e JSON de cada tabela: leads, vendas, CRM, tracking, blog,
                configurações e base de conhecimento.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" onClick={() => void baixarBanco("completo")} disabled={!!dbBusy}>
                  {dbBusy === "completo" ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Database className="h-4 w-4 mr-2" />
                  )}
                  Backup completo
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void baixarBanco("operacao")}
                  disabled={!!dbBusy}
                >
                  {dbBusy === "operacao" ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Só a operação (leve)
                </Button>
              </div>
              <ul className="text-[11px] text-white/50 mt-3 space-y-1">
                <li>
                  <strong className="text-white/70">Completo:</strong> todas as tabelas, incluindo tracking
                  e payloads brutos. Pode levar alguns minutos e gerar um arquivo grande.
                </li>
                <li>
                  <strong className="text-white/70">Só a operação:</strong> exclui page views, cliques,
                  importação do RD e payloads brutos — rápido e suficiente para restaurar o essencial.
                </li>
                <li>Logins e senhas dos usuários não entram no arquivo (ficam na autenticação).</li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
